import { useCallback, useEffect, useState } from 'react';
import {
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Switch,
  Typography,
} from '@material-ui/core';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

/**
 * The weekly team mail in Settings › General (DX-551; specs/weekly-team-mail.md
 * AC-14): the address the mail goes to, and a switch to turn it off and on.
 * The way back for a colleague who unsubscribed through the link in a mail and
 * no longer gets one to undo it from.
 *
 * The address is the one the colleague last signed in with; the catalog's
 * Users carry none. Before their first sign-in since the mail existed the
 * backend knows no address, and the card says it is kept at the next one.
 *
 * MUI v4 only.
 */

type Subscription = { address: string | null; subscribed: boolean };

const PATH = '/weekly-mail/subscription';

export const WeeklyMailCard = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [subscription, setSubscription] = useState<Subscription>();
  const [failed, setFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const ask = useCallback(
    async (init?: RequestInit): Promise<Subscription> => {
      const base = await discoveryApi.getBaseUrl('github-insights');
      const res = await fetchApi.fetch(`${base}${PATH}`, init);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      return (await res.json()) as Subscription;
    },
    [discoveryApi, fetchApi],
  );

  useEffect(() => {
    let current = true;
    ask()
      .then(answer => current && setSubscription(answer))
      .catch(() => current && setFailed(true));
    return () => {
      current = false;
    };
  }, [ask]);

  const turn = async (subscribed: boolean) => {
    setSaving(true);
    setFailed(false);
    try {
      setSubscription(
        await ask({
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subscribed }),
        }),
      );
    } catch {
      setFailed(true);
    } finally {
      setSaving(false);
    }
  };

  const body = () => {
    if (!subscription) {
      return failed ? (
        <Typography variant="body2" color="error">
          The weekly team mail could not be read. Try again later.
        </Typography>
      ) : (
        <Progress />
      );
    }
    if (!subscription.address) {
      return (
        <Typography variant="body2" color="textSecondary">
          No address is known for you yet. The portal keeps the address you sign
          in with, at your next sign-in; from then on you get the weekly mail of
          your teams and can turn it off here.
        </Typography>
      );
    }
    return (
      <List dense disablePadding>
        <ListItem disableGutters>
          <ListItemText
            primary="Weekly mail of your teams"
            secondary={
              subscription.subscribed
                ? `Every Monday to ${subscription.address}: red checks, fallen scorecards and open security findings.`
                : `Turned off. Turn it on to get it at ${subscription.address} again.`
            }
          />
          <ListItemSecondaryAction>
            <Switch
              color="primary"
              checked={subscription.subscribed}
              disabled={saving}
              onChange={event => turn(event.target.checked)}
              inputProps={{ 'aria-label': 'Weekly mail of your teams' }}
            />
          </ListItemSecondaryAction>
        </ListItem>
        {failed && (
          <Typography variant="body2" color="error">
            The change could not be saved. Try again.
          </Typography>
        )}
      </List>
    );
  };

  return <InfoCard title="Weekly team mail">{body()}</InfoCard>;
};
